import React, { useEffect } from 'react';
import { compact } from 'lodash';
import { writeDB } from './store';
import { apollo, useQueryHandler } from './graphql';
import { SCANNER_CODES, SCANNER_CODES_WITH_DELETED, SCANNER_EVENT_QUERY, UPDATE_TICKETS } from './queries/scanner';
import { Seat } from './ticket';
import { useAsyncValue, useData } from './data';
import { useInterval } from '../util/async';
import { useRef, useState } from 'react';
import { useReloadKey } from '../util/react-util';
import { EventScannerCodeType, ScannerCodesWithDeletedQuery, TicketUpdate } from '../generated/graphql';

export interface Ticket {
  id: string;
  eventId: string;
  personalIdentifier: string | null;
  seat: Seat | null;
  validatedBy: string | null;
  validationCode: string;
  usedAt: Date | null;
}

export interface TicketScannerEvent {
  id: string;
  sku: string;
  name: string | null;
  artist: string | null;
  happeningFrom: Date | null;
  stageTime: string | null;
  venue: {
    name: string;
    address: {
      address: string | null;
      address2: string | null;
      city: string | null;
    };
  };
  scannerCodeTypes: EventScannerCodeType[];
  salesStats: {
    sold: number;
  };
}

export type TicketScannerActivityLogItem = {
  id: string;
  eventId: string;
  code: string;
  scannedAt: Date;
  isManual: boolean;
  needsSync: boolean;
} & (
  | {
      type: 'valid';
      ticketId: string;
    }
  | {
      type: 'invalid';
    }
  | {
      type: 'already_checked';
      ticketId: string;
      wasCheckedOut: boolean;
    }
);

export type TicketScannerActivityLogItemWithTicket = TicketScannerActivityLogItem & { ticket?: Ticket };

interface TicketPage {
  tickets: Ticket[];
  deletedTicketIds: string[];
  cursor: string | null;
}

type ScannerProps = {
  secureEventId: string;
  updatedSince: Date | null;
  eventId: string;
};

export function useTicketScanner({ scannerName, secureEventId }: { scannerName: string; secureEventId?: string }): {
  event: TicketScannerEvent | undefined;
  tickets: Ticket[];
  activityLog: TicketScannerActivityLogItemWithTicket[];
  offline: boolean;
  ticketCount: number;
  loadingTickets: boolean;
  loadingEvent: boolean;
} {
  const [offline, setOffline] = useState(false);
  const [, reload] = useReloadKey();
  ticketStore.onUpdate = () => reload();
  const ticketIndex = ticketStore.tickets;
  const tickets = Object.values(ticketIndex);
  const [updatedSince, setUpdatedSince] = useState(null as Date | null);
  const updatedSinceRef = useRef(updatedSince);
  updatedSinceRef.current = updatedSince;

  const { data, loading } = useQueryHandler(SCANNER_EVENT_QUERY, {
    variables: {
      secureEventId,
    },
    onError: () => {
      throw new Error('No event data found');
    },
  });
  const event = data?.event || undefined;
  const eventId = event?.id;

  // Initial load of all tickets.
  const [latestUpdate, loadingTickets] = useAsyncValue(
    async () => await populateTicketStore({ secureEventId, updatedSince: updatedSinceRef.current, eventId }),
    [secureEventId ?? '', eventId ?? ''],
    { setLoadingOnReload: true }
  );

  const [log] =
    useData((db) => [
      db
        .table('activityLog')
        .where({ eventId: data?.event?.id })
        .sort((a, b) => b.scannedAt.getTime() - a.scannedAt.getTime()),
    ]) || [];
  const activityLog = log.map((e) => {
    if (e.type === 'invalid') return e;
    return { ...e, ticket: ticketIndex[e.ticketId] };
  });
  const pendingItems = useRef<TicketScannerActivityLogItem[]>([]);
  pendingItems.current = log.filter((e) => e.needsSync);
  useInterval(3, async () => {
    try {
      secureEventId && (await sync(secureEventId, scannerName, pendingItems.current));
    } catch (error) {
      setOffline(true);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });

  function triggerSync(): void {
    secureEventId && sync(secureEventId, scannerName, pendingItems.current);
  }

  Object.assign(window, { triggerSync });

  // Periodic refresh of all tickets, updated since last load.
  useInterval(60, async () => {
    try {
      await populateTicketStore({ secureEventId, updatedSince: updatedSinceRef.current, eventId });
      setOffline(false);
    } catch (error) {
      setOffline(true);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });

  useEffect(() => {
    if (latestUpdate) {
      setUpdatedSince(latestUpdate);
    }
  }, [latestUpdate]);

  return {
    event,
    tickets,
    activityLog,
    offline,
    loadingTickets,
    loadingEvent: loading,
    ticketCount: tickets.length,
  };
}

function resetLocalScannerData(): void {
  const allIds = writeDB.table('activityLog').all.map((item) => item.id);
  if (allIds.length > 0) writeDB.table('activityLog').delete(allIds);
  ticketStore.clear();
}

async function populateTicketStore({
  secureEventId,
  updatedSince,
  eventId,
}: {
  updatedSince: Date | null;
  eventId?: string | null;
  secureEventId?: string;
}): Promise<Date | null> {
  if (!secureEventId || !eventId) return null;

  if (Object.values(ticketStore.tickets).some((e) => e.eventId !== eventId)) {
    resetLocalScannerData();
    updatedSince = null; // the local data was wiped, so we need a full reload for the new event.
  }

  const nextUpdatedSince = new Date();

  let cursor: string | null = null;
  let reachedEnd = false;
  while (!reachedEnd) {
    const page: TicketPage = await getScannerCodes({ secureEventId, cursor, updatedSince, eventId });
    ticketStore.add(page.tickets);
    ticketStore.delete(page.deletedTicketIds);
    cursor = page.cursor;
    reachedEnd = !page.cursor;
  }

  nextUpdatedSince.setSeconds(nextUpdatedSince.getSeconds() - 3); // use a few seconds of buffer to avoid missing updates.
  return nextUpdatedSince;
}

async function getScannerCodes({
  secureEventId,
  cursor,
  updatedSince,
  eventId,
}: ScannerProps & {
  cursor: string | null;
}): Promise<TicketPage> {
  const result = updatedSince
    ? await apollo.query({
        query: SCANNER_CODES_WITH_DELETED,
        variables: { secureEventId, cursor, updatedSince: updatedSince.toISOString() },
        fetchPolicy: 'no-cache',
      })
    : await apollo.query({
        query: SCANNER_CODES,
        variables: { secureEventId, cursor, updatedSince: null },
        fetchPolicy: 'no-cache',
      });

  if (result.errors) {
    throw new Error(result.errors[0]?.message);
  }

  const tickets: Ticket[] =
    compact(result.data.scannerCodes?.list?.nodes).map((e) => ({
      eventId,
      id: e.id,
      personalIdentifier: e.personalIdentifier,
      seat: e.seat,
      validationCode: e.validationCode,
      validatedBy: e.validatedBy,
      usedAt: e.usedAt ? new Date(e.usedAt) : null,
    })) || [];

  let endCursor = result.data.scannerCodes?.list.pageInfo.endCursor ?? null;
  if (!result.data.scannerCodes?.list.pageInfo.hasNextPage) endCursor = null;

  const deletedTicketIds =
    (result?.data as ScannerCodesWithDeletedQuery | undefined)?.scannerCodes?.deleted?.map((d) => d.id) || [];

  return {
    tickets,
    deletedTicketIds,
    cursor: endCursor,
  };
}

// sadly redux-database is too slow to handle the huge amounts of tickets (yet), therefore we implement a simple datastore here.
class TicketStore {
  onUpdate?: () => void;
  private _tickets: Record<string, Ticket> = {};

  add(tickets: Ticket[]): void {
    tickets.forEach((e) => (this._tickets[e.id] = e));
    this.onUpdate?.();
  }

  delete(ticketIds: string[]): void {
    ticketIds.forEach((id) => delete this._tickets[id]);
    this.onUpdate?.();
  }

  clear(): void {
    this._tickets = {};
    this.onUpdate?.();
  }

  update(ticket: Ticket, values: Partial<Ticket>): void {
    Object.assign(this._tickets[ticket.id], values);
    this.onUpdate?.();
  }

  get tickets(): Record<string, Ticket> {
    return this._tickets;
  }
}

const ticketStore = new TicketStore();

export function checkTicketIn({ ticket, validatedBy }: { ticket: Ticket; validatedBy: string }): void {
  if (!validatedBy) {
    return;
  }
  const usedAt = new Date();
  ticketStore.update(ticket, { usedAt, validatedBy });
}

export function addActivityLogItem(item: TicketScannerActivityLogItem): void {
  writeDB.table('activityLog').insert(item);
}

export function updateActivityLogItem(logItem: Partial<TicketScannerActivityLogItem>): void {
  if (!logItem.id) return;

  writeDB.table('activityLog').update(logItem.id, logItem);
}

export function checkTicketOut({ ticket }: { ticket: Ticket }): void {
  ticketStore.update(ticket, { usedAt: null });
}

async function sync(secureEventId: string, scannerName: string, log: TicketScannerActivityLogItem[]): Promise<void> {
  const ticketUpdates: TicketUpdate[] = compact(
    log.map((e) => {
      const base = { validatedBy: scannerName };
      if (e.type === 'valid') {
        return { ...base, itemId: e.ticketId, usedAt: e.scannedAt.toISOString() };
      } else if (e.type === 'already_checked' && e.wasCheckedOut === true) {
        return { ...base, itemId: e.ticketId, usedAt: null };
      }
      return null;
    })
  );
  if (ticketUpdates.length === 0) return;

  await apollo.mutate({
    mutation: UPDATE_TICKETS,
    variables: {
      secureEventId,
      ticketUpdates,
    },
  });
  log.forEach((e) => {
    if (writeDB.table('activityLog').find(e.id)) {
      writeDB.table('activityLog').update(e.id, { needsSync: false });
    }
  });
}

export const getVenueAddress = (venue: TicketScannerEvent['venue']): string => {
  return [venue.name, venue.address.address, venue.address.address2, venue.address.city]
    .map((e) => e?.trim())
    .filter(Boolean)
    .join(', ');
};

export function useCheckoutAlert(): [boolean, () => void] {
  const [isDisabled, setIsDisabled] = React.useState(false);

  const localStorageKey = 'checkout-alert-disabled';

  const persistAlert = (): void => {
    localStorage.setItem(localStorageKey, JSON.stringify(true));
  };

  useEffect(() => {
    const localStorageValue = JSON.parse(localStorage.getItem(localStorageKey) || 'false');

    setIsDisabled(localStorageValue);
  });

  return [isDisabled, persistAlert];
}
